import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

import "./SignaturePad.css";

const SignaturePad = forwardRef(
  (
    {
      value = "",
      disabled = false,
      onChange,
    },
    ref
  ) => {
    const canvasRef = useRef(null);
    const drawingRef = useRef(false);
    const lastPointRef = useRef(null);
    const [hasSignature, setHasSignature] = useState(Boolean(value));

    const configureContext = (canvas) => {
      const context = canvas.getContext("2d");
      context.lineWidth = 2.25;
      context.lineCap = "round";
      context.lineJoin = "round";
      context.strokeStyle = "#111827";
      return context;
    };

    const resizeCanvas = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const rect = canvas.getBoundingClientRect();
      const ratio = Math.max(window.devicePixelRatio || 1, 1);

      canvas.width = Math.max(1, Math.round(rect.width * ratio));
      canvas.height = Math.max(1, Math.round(rect.height * ratio));

      const context = canvas.getContext("2d");
      context.setTransform(ratio, 0, 0, ratio, 0, 0);
      configureContext(canvas);

      if (value) {
        const image = new Image();
        image.onload = () => {
          context.drawImage(image, 0, 0, rect.width, rect.height);
          setHasSignature(true);
        };
        image.src = value;
      }
    };

    useEffect(() => {
      resizeCanvas();
      const handleResize = () => resizeCanvas();
      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const context = canvas.getContext("2d");
      const rect = canvas.getBoundingClientRect();
      context.clearRect(0, 0, canvas.width, canvas.height);

      if (!value) {
        setHasSignature(false);
        return;
      }

      const image = new Image();
      image.onload = () => {
        context.drawImage(image, 0, 0, rect.width, rect.height);
        setHasSignature(true);
      };
      image.src = value;
    }, [value]);

    const getPoint = (event) => {
      const rect = canvasRef.current.getBoundingClientRect();
      return {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
      };
    };

    const startDrawing = (event) => {
      if (disabled) return;
      event.preventDefault();
      event.currentTarget.setPointerCapture?.(event.pointerId);
      drawingRef.current = true;
      lastPointRef.current = getPoint(event);
    };

    const draw = (event) => {
      if (disabled || !drawingRef.current) return;
      event.preventDefault();

      const canvas = canvasRef.current;
      const context = configureContext(canvas);
      const point = getPoint(event);
      const previousPoint = lastPointRef.current;

      if (!previousPoint) {
        lastPointRef.current = point;
        return;
      }

      context.beginPath();
      context.moveTo(previousPoint.x, previousPoint.y);
      context.lineTo(point.x, point.y);
      context.stroke();

      lastPointRef.current = point;
      setHasSignature(true);
    };

    const finishDrawing = (event) => {
      if (!drawingRef.current) return;

      drawingRef.current = false;
      lastPointRef.current = null;
      event.currentTarget.releasePointerCapture?.(event.pointerId);

      onChange?.(
        canvasRef.current?.toDataURL("image/png") || ""
      );
    };

    const clearSignature = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      canvas
        .getContext("2d")
        .clearRect(0, 0, canvas.width, canvas.height);

      setHasSignature(false);
      onChange?.("");
    };

    useImperativeHandle(ref, () => ({
      clear: clearSignature,
      toDataURL: () =>
        hasSignature
          ? canvasRef.current?.toDataURL("image/png") || ""
          : "",
    }));

    return (
      <div className="signature-pad">
        <div className="signature-pad__canvas-wrap">
          <canvas
            ref={canvasRef}
            className="signature-pad__canvas"
            onPointerDown={startDrawing}
            onPointerMove={draw}
            onPointerUp={finishDrawing}
            onPointerCancel={finishDrawing}
          />

          {!hasSignature && (
            <div className="signature-pad__placeholder">
              Sign here using mouse, trackpad, pen or touch
            </div>
          )}
        </div>

        <div className="signature-pad__actions">
          <span>
            Mouse / trackpad / stylus / touch supported
          </span>

          <button
            type="button"
            disabled={disabled || !hasSignature}
            onClick={clearSignature}
          >
            Clear Signature
          </button>
        </div>
      </div>
    );
  }
);

SignaturePad.displayName = "SignaturePad";

export default SignaturePad;
